import "./polyfills";
import { createApp } from "vue";
import { createRouter, createWebHistory, START_LOCATION } from "vue-router";
import * as Sentry from "@sentry/vue";
import posthog from "posthog-js";
import VueAuth0Plugin, {
  AuthenticationGuard,
  AuthenticationGuardWithoutLoginRedirect,
  AuthenticationProperties,
} from "vue-auth0-plugin";
import VueClipboard from "vue-clipboard2";
import { toastPlugin } from "./composables/useToast";
import { createHead } from "@vueuse/head";
import Markdown from "vue3-markdown-it";
import VueMobileDetection from "vue-mobile-detection";
import Vue3Sanitize from "vue-3-sanitize";
import {
  logAdSkipped,
  reportPurchase,
  reportSubscriptionStarted,
  reportTrialStarted,
  setGoogleUserData,
} from "./helpers/ads-conversions";
import {
  captureClickId,
  capturePartnerStackClickId,
} from "./helpers/click-ids";
import { trackMilestone } from "./composables/useAnalytics";
import { fixPartnerReferrer, recordOwnUrl } from "./helpers/partner-return";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faMemory,
  faMicrochip,
  faDisplay,
  faHardDrive,
  faInfoCircle,
  faTerminal,
  faGaugeHigh,
  faChartBar,
  faChartLine,
  faServer,
  faClockRotateLeft,
  faLaptop,
  faWindowMaximize,
  faBackward,
  faCirclePlay,
  faNetworkWired,
  faBug,
  faComments,
  faRobot,
  faMousePointer,
  faKeyboard,
  faArrowsUpDown,
  faHandPointer,
  faHandRock,
  faCheckCircle,
  faBrain,
  faClock,
  faHourglassHalf,
  faImage,
  faSearch,
  faWindowRestore,
  faCircle,
  faCode,
  faUpDownLeftRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faMarkdown,
  faApple,
  faChrome,
} from "@fortawesome/free-brands-svg-icons";
import VueVirtualScroller from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

import store from "./store";

import "./index.css";

import App from "./App.vue";

// Every page is lazy-loaded so each route becomes its own chunk. The initial
// bundle only carries App.vue + the router; heavy pages (Replay's charts and
// xterm, the dashboard, the eve workspace) download on first visit instead of
// on every cold load.
const Dashboard = () => import("./pages/Dashboard.vue");
const Replays = () => import("./pages/dashboard/projects/Replays.vue");
const TestRuns = () => import("./pages/dashboard/TestRuns.vue");
const PrReviews = () => import("./pages/dashboard/PrReviews.vue");
const TestCases = () => import("./pages/dashboard/TestCases.vue");
const TestMetrics = () => import("./pages/dashboard/TestMetrics.vue");
const TestRunDetail = () => import("./pages/dashboard/TestRunDetail.vue");
const Replay = () => import("./pages/Replay.vue");
const Releases = () => import("./pages/Releases.vue");
const PrivacyPolicy = () => import("./pages/legal/PrivacyPolicy.vue");
const TermsOfService = () => import("./pages/legal/TermsOfService.vue");
const EmbeddedNotifier = () => import("./pages/EmbeddedNotifier.vue");
const Onboarding = () => import("./pages/onboarding/Onboarding.vue");
const JoinBeta = () => import("./pages/onboarding/JoinBeta.vue");
const Team = () => import("./pages/Team.vue");
const TeamSettings = () => import("./pages/team/Settings.vue");
const TeamApiKeys = () => import("./pages/team/ApiKeys.vue");
const TeamGithub = () => import("./pages/team/GitHub.vue");
const BillingPlan = () => import("./pages/billing/Plan.vue");
const Checkout = () => import("./pages/Checkout.vue");
const Usage = () => import("./pages/Usage.vue");
const Cache = () => import("./pages/Cache.vue");
const Coverage = () => import("./pages/Coverage.vue");
const Stats = () => import("./pages/Stats.vue");
const Invite = () => import("./pages/onboarding/Invite.vue");
const Auth0Callback = () => import("./pages/Auth0Callback.vue");
const AcceptTerms = () => import("./pages/AcceptTerms.vue");
const Login = () => import("./pages/Login.vue");
const Device = () => import("./pages/Device.vue");
const SystemStatus = () => import("./pages/SystemStatus.vue");
const NotFound = () => import("./pages/NotFound.vue");
const EveExplore = () => import("./pages/EveExplore.vue");
const SetupLayout = () => import("./pages/setup/SetupLayout.vue");
const SetupConnect = () => import("./pages/setup/SetupConnect.vue");
const SetupGenerate = () => import("./pages/setup/SetupGenerate.vue");
const SetupDeploy = () => import("./pages/setup/SetupDeploy.vue");
const ConnectLayout = () => import("./pages/github/ConnectLayout.vue");
const Start = () => import("./pages/start/Start.vue");
const GithubConnect = () => import("./pages/github/GithubConnect.vue");
const BuildLayout = () => import("./pages/build/BuildLayout.vue");
const BuildPrompt = () => import("./pages/build/BuildPrompt.vue");
const BuildGenerate = () => import("./pages/build/BuildGenerate.vue");
import VueHighlightJS from "vue3-highlightjs";
/* add icons to the library */
library.add(
  faMemory,
  faMicrochip,
  faDisplay,
  faHardDrive,
  faMarkdown,
  faApple,
  faBackward,
  faTerminal,
  faGaugeHigh,
  faChartBar,
  faChartLine,
  faServer,
  faClockRotateLeft,
  faLaptop,
  faWindowMaximize,
  faCirclePlay,
  faInfoCircle,
  faChrome,
  faNetworkWired,
  faBug,
  faComments,
  faRobot,
  faMousePointer,
  faKeyboard,
  faArrowsUpDown,
  faHandPointer,
  faHandRock,
  faCheckCircle,
  faBrain,
  faClock,
  faHourglassHalf,
  faImage,
  faSearch,
  faWindowRestore,
  faCircle,
  faCode,
  faUpDownLeftRight
);

const routes = [
  {
    name: "auth0-callback",
    path: "/auth0-callback",
    component: Auth0Callback,
    beforeEnter: AuthenticationGuard,
  },
  {
    name: "accept-terms",
    path: "/accept-terms",
    component: AcceptTerms,
    beforeEnter: AuthenticationGuard,
  },
  {
    // Public login landing. Popups need a user gesture, so guards that used to
    // fire loginWithRedirect now send anonymous users here; the "Continue"
    // button triggers loginWithPopup and resumes auth_return_to afterward.
    name: "login",
    path: "/login",
    component: Login,
  },
  {
    name: "device",
    path: "/device/:deviceCode",
    component: Device,
    beforeEnter: AuthenticationGuard,
  },
  {
    // The GitHub App's configured Setup URL. GitHub appends
    // ?installation_id=…&setup_action=… and lands here; the GitHub-first funnel
    // owns the post-install repo picker, so forward the whole query through.
    // Kept as its own path because it's registered with GitHub and can't be
    // changed without a round-trip through the App settings.
    name: "github-setup",
    path: "/github-setup",
    redirect: (to) => ({ name: "github-connect", query: to.query }),
  },
  {
    // The GitHub-first onboarding funnel the homepage now leads with. Its own
    // logo-only shell (no wizard progress bar), unguarded like /setup: the repo
    // picker is public and the sign-in gate is a modal on the confirm button.
    path: "/github",
    component: ConnectLayout,
    children: [
      { name: "github-connect", path: "", component: GithubConnect },
    ],
  },
  {
    // Sign-up-first onboarding: the marketing "Get started" CTA lands here.
    // Anonymous visitors go straight to Auth0's sign-up screen (same pattern as
    // /checkout), then come back to the qualification questions, a quickstart
    // picker, and — for qualified teams — an embedded demo calendar.
    path: "/start",
    component: ConnectLayout,
    beforeEnter: async (to, from) => {
      if (await AuthenticationGuardWithoutLoginRedirect(to, from)) return true;
      sessionStorage.setItem("auth_return_to", to.fullPath);
      await AuthenticationProperties.loginWithRedirect({
        screen_hint: "signup",
        appState: { targetUrl: to.fullPath },
      });
      return false;
    },
    children: [{ name: "start", path: "", component: Start }],
  },
  {
    name: "checkout",
    path: "/checkout/:planId",
    component: Checkout,
    // Stash the intended checkout path *before* AuthenticationGuard bounces to
    // Auth0. Checkout.vue's created() does the same thing, but it never runs on
    // a cold anonymous arrival — the guard redirects away before the component
    // is instantiated. That matters now that the marketing pricing page links
    // straight here: without this, the plan and ?trialDays are lost across
    // login, and the /accept-terms detour a brand-new signup takes lands them
    // on /team instead of Stripe. Both Auth0Callback.vue and AcceptTerms.vue
    // resume from this key.
    //
    // Anonymous visitors here are almost always new (an "Upgrade to unlock"
    // click or a pricing-page link), so send them to Auth0's sign-up screen
    // rather than the plugin guard's default login screen. Auth0's sign-up page
    // still links to log in for returning users.
    beforeEnter: async (to, from) => {
      sessionStorage.setItem("auth_return_to", to.fullPath);
      if (await AuthenticationGuardWithoutLoginRedirect(to, from)) return true;
      await AuthenticationProperties.loginWithRedirect({
        screen_hint: "signup",
        appState: { targetUrl: to.fullPath },
      });
      return false;
    },
  },
  {
    name: "replay",
    path: "/replay/:replayId",
    component: Replay,
    props: (route) => ({
      replayId: route.params.replayId,
      shareKey: route.query.share,
      isEmbedded: route.query.embed === "true",
    }),
  },
  {
    name: "replay-share",
    path: "/share/:shareKey",
    component: Replay,
    props: (route) => ({
      shareKey: route.params.shareKey,
      isEmbedded: route.query.embed === "true",
    }),
  },
  {
    name: "beta",
    path: "/beta",
    component: JoinBeta,
    beforeEnter: AuthenticationGuard,
  },
  {
    name: "stats",
    path: "/stats",
    component: Stats,
  },
  {
    // The public onboarding wizard. Deliberately NOT a Dashboard child and
    // deliberately unguarded: step 1 (connect your app) is reachable with no
    // account at all, and the sign-in gate is a modal inside step 1 rather than
    // a route guard, so a visitor never loses the answers they already gave.
    path: "/setup",
    component: SetupLayout,
    redirect: { name: "setup-connect" },
    children: [
      { name: "setup-connect", path: "connect", component: SetupConnect },
      {
        name: "setup-generate",
        path: "generate/:sessionId?",
        component: SetupGenerate,
      },
      { name: "setup-deploy", path: "deploy", component: SetupDeploy },
    ],
  },
  {
    // build.testdriver.ai: describe an app, the agent builds it and tests it.
    // Public like /setup; sign-in is a modal on submit. Reachable at /build on
    // any host so previews work without the custom domain.
    path: "/build",
    component: BuildLayout,
    children: [
      { name: "build-prompt", path: "", component: BuildPrompt },
      {
        name: "build-generate",
        path: "generate/:sessionId?",
        component: BuildGenerate,
      },
    ],
  },
  {
    // Kept as a redirect: already-published marketing builds and shared links
    // point here, and everyone now goes straight to the wizard.
    path: "/onboarding",
    redirect: { name: "setup-connect" },
  },
  {
    // Kept as a redirect: the demo used to live at /eve-explore, and shared
    // links / older marketing builds still point there.
    path: "/eve-explore/:sessionId?",
    redirect: { name: "setup-connect" },
  },
  {
    // The demo is now step 2 of the /setup wizard, which reaches the same
    // workspace with the connect step's answers already in hand. Signed-in
    // users with a session id keep landing on /generate, which is that same
    // workspace without the wizard chrome.
    path: "/demo/:sessionId?",
    redirect: (to) =>
      to.params.sessionId
        ? { name: "generate", params: to.params }
        : { name: "setup-connect" },
  },
  {
    name: "dashboard-layout",
    path: "/",
    component: Dashboard,
    beforeEnter: AuthenticationGuard,
    redirect: "/generate",
    children: [
      {
        name: "testdriver",
        path: "testdriver",
        component: {
          created() {
            this.$store.commit("setSource", "testdriver");
            this.$router.push("/team");
          },
          render() {
            return null;
          },
        },
      },
      {
        name: "projects",
        path: "projects",
        component: {
          async created() {
            await this.$store.dispatch("fetchProjects");
            const projects = this.$store.state.projects;
            if (projects?.length > 0) {
              this.$router.replace({
                name: "replays",
                params: { projectId: projects[0].id },
              });
            }
          },
          render() {
            return null;
          },
        },
      },
      {
        name: "replays",
        path: "projects/:projectId",
        component: Replays,
      },
      {
        name: "team",
        path: "/team",
        component: Team,
      },
      {
        name: "team-settings",
        path: "/team/settings",
        component: TeamSettings,
      },
      {
        name: "team-api-keys",
        path: "/team/api-keys",
        component: TeamApiKeys,
      },
      {
        name: "team-github",
        path: "/team/github",
        component: TeamGithub,
      },
      {
        name: "billing-plan",
        path: "/billing",
        component: BillingPlan,
      },
      // Settings was split into Team and Billing pages. Emails, Stripe return
      // URLs and API errors still link here with ?checkout= / ?source=, so keep
      // the query when forwarding.
      {
        name: "settings",
        path: "/settings",
        redirect: (to) => ({ name: "billing-plan", query: to.query }),
      },
      {
        name: "usage",
        path: "/usage",
        component: Usage,
      },
      {
        name: "cache",
        path: "/cache",
        component: Cache,
      },
      {
        name: "cache-detail",
        path: "/cache/:id",
        component: Cache,
      },
      {
        name: "coverage",
        path: "/coverage",
        component: Coverage,
      },
      // {
      //   name: "coverage-insights",
      //   path: "/coverage-insights",
      //   component: CoverageInsights,
      // },
      {
        name: "runs",
        path: "/runs",
        component: TestRuns,
      },
      {
        name: "pr-reviews",
        path: "/reviews",
        component: PrReviews,
      },
      {
        name: "tests",
        path: "/tests",
        component: TestCases,
      },
      {
        name: "metrics",
        path: "/metrics",
        component: TestMetrics,
      },
      {
        name: "test-run-detail",
        path: "/runs/:runId/:testCaseId?",
        component: TestRunDetail,
      },
      {
        name: "generate",
        path: "/generate/:sessionId?",
        component: EveExplore,
      },
      {
        name: "onboarding",
        path: "/welcome",
        component: Onboarding,
      },
    ],
  },
  {
    name: "beta",
    path: "/beta",
    component: JoinBeta,
  },
  {
    path: "/invite",
    component: Invite,
  },
  {
    name: "releases",
    path: "/releases",
    component: Releases,
  },
  {
    name: "embeddedNotifier",
    path: "/embedded-notifier",
    component: EmbeddedNotifier,
  },
  {
    name: "privacyPolicy",
    path: "/legal/privacy-policy",
    component: PrivacyPolicy,
  },
  {
    name: "termsOfService",
    path: "/legal/terms-of-service",
    component: TermsOfService,
  },
  {
    name: "systemStatus",
    path: "/system-status",
    component: SystemStatus,
  },
  {
    name: "notFound",
    path: "/:pathMatch(.*)*",
    component: NotFound,
  },
];

const head = createHead();

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// On build.testdriver.ai the bare root is the builder, not the dashboard.
const isBuildHost = window.location.hostname.startsWith("build.");
router.beforeEach((to) => {
  // "/" redirects to /generate before guards run, so check where it came from.
  const path = to.redirectedFrom?.path ?? to.path;
  if (isBuildHost && path === "/") return { name: "build-prompt" };
});

// Record the ad click id on arrival, before any navigation strips the query
// string. Needed to attribute the server-side purchase conversion, which is
// uploaded later from the Stripe webhook with no browser left to read a cookie.
captureClickId();
capturePartnerStackClickId();

// Page view tracking with PostHog. posthog.init sets capture_pageview: false,
// so this is the only source of $pageview, including the first one on load.
router.afterEach((to, from) => {
  // So a return from Auth0 / Stripe / GitHub can credit this page as referrer.
  recordOwnUrl();

  // Skip if same page (e.g., hash change only). START_LOCATION's path is "/",
  // so the initial navigation is checked explicitly or a landing on "/" is lost.
  if (from !== START_LOCATION && to.path === from.path) return;

  if (window.posthog) {
    window.posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: to.path,
      name: to.name,
      referrer: from.path || document.referrer
    });
  }
});

// Report the Google Ads paid-conversion when Stripe hands the browser back.
//
// This lives on the router rather than on a page because the landing differs
// per flow: the in-console upgrade returns to /settings, and a usage-limit
// upgrade returns there too. Keying on the query string catches both, and any
// future one.
//
// `session_id` is required, not optional: it's the transaction_id Google
// dedupes on, and it's what makes this fire once per checkout instead of once
// per page view (see the note in Team.vue).
//
// With no trial, this fires on a REAL paid checkout: Stripe has charged the first
// invoice by the time it hands the browser back, so `value_cents` below is
// genuine revenue, reported to the Google Ads "Purchase" conversion action
// (GOOGLE_LABELS.purchase in ads-conversions.js).
function reportPosthogSubscriptionStarted(query) {
  try {
    const sessionId = String(query.session_id);
    const seenKey = `posthog_subscription_started:${sessionId}`;
    try {
      if (sessionStorage.getItem(seenKey)) return;
    } catch (e) {
      /* storage unavailable; capture anyway */
    }
    const cents = Number(query.value_cents);
    trackMilestone('Subscription Started', 'subscribed', {
      reported_from: 'browser',
      checkoutSessionId: sessionId,
      plan: query.plan || undefined,
      trial: query.trial === '1',
      value: Number.isFinite(cents) ? cents / 100 : undefined,
      teamId: store.state.user?.team?.id,
    });
    try {
      sessionStorage.setItem(seenKey, '1');
    } catch (e) {
      /* storage unavailable */
    }
  } catch (e) {
    /* never let analytics break the checkout return */
  }
}

// Second half of the Stripe event pair ("Checkout Redirected to Stripe" is the
// first), for both outcomes, so a funnel shows drop-off and time on Stripe.
// Only on a fresh page load: that is how Stripe hands the browser back.
router.afterEach((to, from) => {
  if (from !== START_LOCATION) return;
  const status = to.query.checkout;
  if (status !== 'success' && status !== 'cancelled') return;
  window.posthog?.capture('Checkout Returned', {
    status,
    checkoutSessionId: to.query.session_id ? String(to.query.session_id) : undefined,
    plan: to.query.plan || undefined,
  });
});

router.afterEach((to) => {
  if (to.query.checkout !== 'success') return;
  if (!to.query.session_id) {
    // A success return with no session id means the success_url lost Stripe's
    // {CHECKOUT_SESSION_ID} substitution. Nothing can be keyed, so nothing fires.
    logAdSkipped(
      'all',
      'checkout-return',
      'checkout=success arrived without a session_id, so no conversion can be keyed',
      { path: to.fullPath }
    );
    return;
  }

  // X (Twitter) "Subscription Started" pixel. Fires on any completed checkout
  // (trial or paid), the moment the subscription is created. The server-side
  // Conversion API reports the same event from the checkout.session.completed
  // webhook (twitter-conversions EVENTS.SUBSCRIPTION_STARTED) with the same
  // conversion_id, so X dedupes the two. Best-effort: twq is a third-party script
  // ad blockers routinely remove.
  reportSubscriptionStarted(String(to.query.session_id));

  // PostHog "Subscription Started", from the identified browser session. The
  // Stripe webhook captures the same event name server-side under the owner's
  // id; `reported_from: 'browser'` tells the two apart. Latched per session id
  // so a reload of the success page doesn't capture it again.
  reportPosthogSubscriptionStarted(to.query);

  // Enhanced Conversions: attach the hashed email to the Google conversions below.
  setGoogleUserData(store.state.user);

  // Free-trial checkouts (the GitHub "Try free for 14 days" flow) return at $0:
  // Stripe hasn't charged, so firing the revenue conversion now would over-report.
  // The real purchase conversion is reported when the trial converts: X and
  // Reddit server-side from the invoice.paid webhook, and every platform's
  // browser pixel by reportPendingPurchase() on the owner's next console load. Instead fire the trial-signup conversion —
  // an event (valued by path, see STEP_VALUES) Google bids on as "started a trial" — and stop here so the
  // revenue pixel below never runs for a trial.
  if (to.query.trial === '1') {
    reportTrialStarted(store.state.user, to.query.session_id);
    logAdSkipped(
      'google+reddit',
      'purchase',
      'trial checkout charged $0; the purchase is reported when the trial converts (invoice.paid)',
      { session_id: String(to.query.session_id) },
      { routine: true }
    );
    return;
  }

  // `value_cents` is stamped by the checkout controller with the price actually
  // billed, which is not necessarily the plan's list price while the price
  // experiment is running. Fall back to no value rather than guessing wrong.
  const cents = Number(to.query.value_cents);
  const value = Number.isFinite(cents) && cents > 0 ? cents / 100 : undefined;

  // Google Ads and Reddit Purchase. The invoice.paid webhook reports the
  // server-side copies under the same Checkout Session id, so each platform
  // dedupes the pair. X's pixel waits for reportPendingPurchase() on the next
  // user refresh, once the webhook has stamped the plan's LTV it reports.
  reportPurchase({
    transactionId: String(to.query.session_id),
    value,
    currency: 'USD',
    platforms: ['google', 'xCharged', 'reddit'],
  });
});

const authEnv = {
  audience: `${import.meta.env.VITE_AUTH0_DOMAIN}/api/v2/`,
  scopes: "given_name profile email offline_access",
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth0-callback`,
  useRefreshTokens: true,
};

const isEmbedded = window.location.href.endsWith("embed=true");

const app = createApp(App);

// A local dev server (Vite on localhost / 127.0.0.1) captures exceptions from a
// developer's uncommitted work in progress. That code never ships, so its
// errors only add triage noise to error tracking. Guard exception capture on the
// host name, which covers the loopback addresses and *.localhost / *.local.
const isLocalHost = (host) => {
  if (!host) return false;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "[::1]" ||
    host === "::1" ||
    host.endsWith(".localhost") ||
    host.endsWith(".local")
  );
};

// Initialize Sentry
const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.CONTEXT || 'development';
const isSentryDisabled = import.meta.env.TD_CHANNEL === 'test' || import.meta.env.IS_PULL_REQUEST === 'true';
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.01,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.testdriver\.ai/],
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.01,
  environment:
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    import.meta.env.CONTEXT ||
    "development",
  release: import.meta.env.APP_VERSION,
  enabled: !!import.meta.env.VITE_SENTRY_DSN && !isSentryDisabled,
  beforeSend(event, hint) {
    // Drop exceptions raised on a local dev server (see isLocalHost): they come
    // from a developer's uncommitted work in progress, never a deployed build.
    if (isLocalHost(window.location.hostname)) {
      return null;
    }
    // Filter out noise from third-party / browser-extension logging beacons
    // (e.g. a minified JSLoggerClient / LogBeacon that emits a 401). These do
    // not originate from TestDriver code, so they shouldn't be tracked as app
    // errors.
    const originalException = hint && hint.originalException;
    const frames =
      event.exception &&
      event.exception.values &&
      event.exception.values[0] &&
      event.exception.values[0].stacktrace &&
      event.exception.values[0].stacktrace.frames;
    const haystack = [
      originalException && originalException.stack,
      ...(frames || []).map((f) => `${f.function || ""} ${f.filename || ""}`),
    ]
      .filter(Boolean)
      .join(" ");

    if (/JSLoggerClient|LogBeacon/.test(haystack)) {
      return null;
    }
    return event;
  },
});

// Initialize PostHog
posthog.init("phc_ovOQ2HFpqFwPVqaxE5plJmI9buPMsz024FB2iajItqn", {
  api_host: "https://d.testdriver.ai",
  person_profiles: "always",
  // Share the distinct_id cookie across *.testdriver.ai so a visitor who lands
  // on the marketing site and then signs up here keeps one person profile,
  // carrying their original UTM params and referrer through identify().
  // Must stay in sync with the init in marketing/astro/layouts/BaseLayout.astro.
  cross_subdomain_cookie: true,
  // The router's afterEach captures every $pageview, including the first, with
  // route props. Leaving the automatic one on double-counted every landing.
  capture_pageview: false,
  // Defaults to following capture_pageview, so keep it on explicitly.
  capture_pageleave: true,
  disable_web_experiments: false,
  session_recording: {
    enabled: true,
    recordCrossOriginIframes: true,
  },
  before_send: (event) => {
    // Exception autocapture is on for this project, so posthog-js reports
    // client errors as $exception events. Drop the ones raised on a local dev
    // server (see isLocalHost) so a developer's uncommitted work in progress
    // does not reach error tracking as issues no deployed user can hit.
    if (event?.event === "$exception" && isLocalHost(window.location.hostname)) {
      return null;
    }
    // Drop the posthog-js SDK's own request timeout. The SDK retries the
    // request, so no user flow breaks, and each SDK release opens a new issue.
    if (
      event?.event === "$exception" &&
      (event.properties?.$exception_list || []).some((e) =>
        e?.value?.startsWith?.("PostHog request timed out"),
      )
    ) {
      return null;
    }
    // Returning from Auth0, Stripe or a GitHub install: credit the console page
    // they left from, not the partner (see helpers/partner-return.js).
    return fixPartnerReferrer(event);
  },
  loaded: (posthog) => {
    if (import.meta.env.DEV) posthog.debug();
  },
});

// Make PostHog available globally
window.posthog = posthog;

app
  .component("font-awesome-icon", FontAwesomeIcon)
  .use(store)
  .use(router)
  .use(VueClipboard)
  .use(Markdown)
  .use(toastPlugin)
  .use(VueHighlightJS)
  .use(head)
  .use(VueMobileDetection)
  .use(Vue3Sanitize, {
    allowedAttributes: {
      "*": ["style"],
    },
  })
  .use(VueVirtualScroller);

if (!isEmbedded) {
  app.use(VueAuth0Plugin, authEnv);
}
// Log popstate events (browser back/forward)
window.addEventListener("popstate", function (event) {
  console.log("Navigation (popstate):", window.location.href);
});
app.mount("#app");
